import { ShieldAlert, AlertCircle, Lock, AlertOctagon, CreditCard, MessageSquare } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

interface ErrorViewProps {
  onRetry?: () => void;
}

interface BillingErrorProps {
  billingType?: string;
  message?: string;
  remainingConversations?: number;
  type?: string;
}

export function UnauthorizedView({ onRetry }: ErrorViewProps) {
  return (
    <div className="h-screen w-full flex items-center justify-center px-4 bg-black/90">
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-2">
          <div className="flex justify-center">
            <Lock className="h-24 w-24 text-red-500" />
          </div>
          <h1 className="text-6xl font-extrabold text-red-500">401</h1>
          <p className="text-3xl font-semibold text-red-400">Unauthorized</p>
        </div>
        <div className="mt-4 bg-white/10 backdrop-blur-sm shadow-2xl rounded-3xl p-8 space-y-6">
          <p className="text-gray-300">
            you're not authorized to access this conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="flex-1 bg-red-600 hover:bg-red-700">
              <Link href="/login">Sign In</Link>
            </Button>
            {onRetry && (
              <Button
                variant="outline"
                onClick={onRetry}
                className="flex-1 border-red-600 text-red-400 hover:bg-red-950"
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ForbiddenView() {
  return (
    <div className="h-screen w-full flex items-center justify-center px-4 bg-black/90">
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-2">
          <div className="flex justify-center">
            <ShieldAlert className="h-24 w-24 text-red-500" />
          </div>
          <h1 className="text-6xl font-extrabold text-red-500">403</h1>
          <p className="text-3xl font-semibold text-red-400">
            Access Forbidden
          </p>
        </div>
        <div className="mt-4 bg-white/10 backdrop-blur-sm shadow-2xl rounded-3xl p-8 space-y-6">
          <p className="text-gray-300">
            Sorry, but you don't have permission to access this conversation.
          </p>
          <Button asChild className="w-full bg-red-600 hover:bg-red-700">
            <Link href="/">Return Home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function NotFoundView() {
  return (
    <div className="h-screen w-full flex items-center justify-center px-4 bg-black/90">
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-2">
          <div className="flex justify-center">
            <AlertCircle className="h-24 w-24 text-yellow-500" />
          </div>
          <h1 className="text-6xl font-extrabold text-yellow-500">404</h1>
          <p className="text-3xl font-semibold text-yellow-400">Not Found</p>
        </div>
        <div className="mt-4 bg-white/10 backdrop-blur-sm shadow-2xl rounded-3xl p-8 space-y-6">
          <p className="text-gray-300">
            The conversation you're looking for doesn't exist or has been
            removed.
          </p>
          <Button asChild className="w-full bg-yellow-600 hover:bg-yellow-700">
            <Link href="/">Return Home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TooManyRequestsView({ billingType, message, remainingConversations, type }: BillingErrorProps) {
  // Determine if this is a billing-related issue
  const isBillingRelated = !!billingType;
  // Determine if this is a conversation-specific issue
  const isConversationRelated = type === 'conversation';
  
  let defaultMessage = "Conversation cannot be continued, please wait a moment and try again.";
  if (isBillingRelated) {
    defaultMessage = "We've reached the conversation limit for this form.";
  } else if (isConversationRelated) {
    defaultMessage = "This operation failed because you are out of conversation credits.";
  }
    
  const displayMessage = message || defaultMessage;
  
  return (
    <div className="h-screen w-full flex items-center justify-center px-4 bg-black/90">
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-2">
          <div className="flex justify-center">
            {isConversationRelated ? (
              <MessageSquare className="h-24 w-24 text-orange-500" />
            ) : isBillingRelated ? (
              <CreditCard className="h-24 w-24 text-orange-500" />
            ) : (
              <AlertOctagon className="h-24 w-24 text-orange-500" />
            )}
          </div>
          <h1 className="text-6xl font-extrabold text-orange-500">429</h1>
          <p className="text-3xl font-semibold text-orange-400">
            {isConversationRelated ? 'Conversation Limit Reached' : isBillingRelated ? 'Form Limit Reached' : 'Too Many Requests'}
          </p>
        </div>
        <div className="mt-4 bg-white/10 backdrop-blur-sm shadow-2xl rounded-3xl p-8 space-y-6">
          <p className="text-gray-300">
            {displayMessage}
          </p>
          
          {isConversationRelated && (
            <div className="mt-6 space-y-4">
              <div className="py-3 px-4 bg-orange-950/50 rounded-lg border border-orange-500/20">
                <h3 className="text-sm font-semibold text-orange-400 mb-2">What happened?</h3>
                <p className="text-sm text-gray-300">
                  You've reached your limit for conversation analysis operations like sentiment analysis, 
                  summaries, or suggestions. This is a limitation of your current plan.
                </p>
              </div>
              
              <div className="py-3 px-4 bg-orange-950/50 rounded-lg border border-orange-500/20">
                <h3 className="text-sm font-semibold text-orange-400 mb-2">What can you do?</h3>
                <p className="text-sm text-gray-300">
                  To continue using these features, please upgrade your plan for additional conversation credits.
                  Your existing conversations will remain accessible.
                </p>
              </div>
            </div>
          )}
          
          {isBillingRelated && !isConversationRelated && (
            <div className="mt-6 space-y-4">
              <div className="py-3 px-4 bg-orange-950/50 rounded-lg border border-orange-500/20">
                <h3 className="text-sm font-semibold text-orange-400 mb-2">What happened?</h3>
                <p className="text-sm text-gray-300">
                  The form owner has reached their monthly conversation limit. 
                  This is a limitation of their current plan, not an issue with your submission.
                </p>
              </div>
              
              <div className="py-3 px-4 bg-orange-950/50 rounded-lg border border-orange-500/20">
                <h3 className="text-sm font-semibold text-orange-400 mb-2">What can you do?</h3>
                <p className="text-sm text-gray-300">
                  Please contact the form owner and let them know you encountered 
                  this limit. They can upgrade their plan or wait until their quota resets.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="flex-1 bg-orange-600 hover:bg-orange-700">
              <Link href="/">Return Home</Link>
            </Button>
            {!isBillingRelated && !isConversationRelated && (
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="flex-1 border-orange-500 text-orange-400 hover:bg-orange-950/30"
              >
                Try Again
              </Button>
            )}
          </div>
        </div>
        
        {(isBillingRelated || isConversationRelated) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-white/5 backdrop-blur-sm rounded-xl p-4 mt-4"
          >
            <p className="text-sm text-gray-400 mb-2">
              {isConversationRelated ? 'Conversation Credits' : 'Form Details'}
            </p>
            <p className="text-xs text-gray-500">
              Plan Type: <span className="font-semibold text-orange-400">{billingType}</span>
              {remainingConversations !== undefined && (
                <> • Remaining: <span className="font-semibold text-orange-400">{remainingConversations}</span></>
              )}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
